import { useState } from 'react';

import { colors } from '@EVOCORP/corechat-theme';
import {
  ListItem,
  Paper,
  Grid,
  Box,
  Typography,
  Tooltip,
  ListItemText,
  makeStyles,
  alpha,
} from '@material-ui/core';
import { CheckCircle, Cancel, Info } from '@material-ui/icons';
import { VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { usePhoneInput } from 'react-international-phone';
import { useModal } from 'react-modal-hook';

import { BlockedContactBadge } from 'components/BlockedContactBadge';
import { BlockedContactName } from 'components/BlockedContactName';
import { ContactAvatar } from 'components/ContactAvatar';
import ImageViewer from 'components/ImageViewer';
import TagsV2 from 'components/TagsV2/TagsV2';
import { TipoCanalEnum } from 'store/reducers/canais';
import {
  ContatoInterfaceV2,
  ContatoStatusTypes,
} from 'store/reducers/contatos';
import getIconTipoCanal from 'utils/getIconTipoCanal';

import ContatoMenuAcoes from './ContatoMenuAcoes';
import DrawerDetalhesContato from './DrawerDetalhesContato';

const useStyles = makeStyles(({ spacing, palette }) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  list: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'relative',
    marginTop: spacing(1),
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    padding: spacing(1, 2),
    marginRight: spacing(1),

    '&:hover': {
      cursor: 'pointer',
      backgroundColor: palette.action.hover,
    },
  },
  paperSelected: {
    '&&': {
      backgroundColor: alpha(palette.primary.main, 0.1),
    },
  },
  avatarWrapper: {
    position: 'relative',
    display: 'inline-flex',
  },
}));

interface ContatoListItemProps {
  contato: ContatoInterfaceV2;
  rowVirtualizer: Virtualizer<HTMLUListElement, Element>;
  virtualRow: VirtualItem;
  onRefresh: () => void;
  selected: boolean;
  onToggleActionSelection: () => void;
}

export default function ContatoListItem({
  contato,
  rowVirtualizer,
  virtualRow,
  onRefresh,
  selected,
  onToggleActionSelection,
}: ContatoListItemProps) {
  const classes = useStyles();
  const [contatoSelected, setContatoSelected] = useState<ContatoInterfaceV2>();

  const {
    id,
    fotoUrl,
    name,
    contact: numContato,
    status,
    tipoCanal,
    blockedAt,
  } = contato;

  const isBlocked = Boolean(blockedAt);

  const { phone } = usePhoneInput({
    defaultCountry: 'br',
    value: numContato ?? '',
  });

  const isInstagramOrMessengerChannel =
    tipoCanal === TipoCanalEnum.INSTAGRAM ||
    tipoCanal === TipoCanalEnum.MESSENGER;

  const displayContato = isInstagramOrMessengerChannel ? numContato : phone;

  const [showDrawerEditarContato, hideDrawerEditarContato] = useModal(
    ({ in: open }) => {
      if (!contatoSelected) return null;

      return (
        <DrawerDetalhesContato
          open={open}
          onClose={hideDrawerEditarContato}
          contatoId={contatoSelected.id}
        />
      );
    },
    [contatoSelected],
  );

  const handleExibirDetalhes = (contato: ContatoInterfaceV2) => {
    setContatoSelected(contato);
    showDrawerEditarContato();
  };

  const getEquipeUsuarioLabel = (contato: ContatoInterfaceV2) => {
    if (contato.teamDefault) {
      if (contato.atendenteDefault) {
        return `${contato.teamDefault.name} - ${contato.atendenteDefault.name}`;
      }
      return contato.teamDefault.name;
    }
    return ' - ';
  };

  const renderStatusIcon = (status: ContatoStatusTypes) => {
    switch (status) {
      case 'VALIDO':
        return (
          <Tooltip arrow title="Contato válido">
            <CheckCircle color="disabled" style={{ width: 16, height: 16 }} />
          </Tooltip>
        );

      case 'INVALIDO':
        return (
          <Tooltip arrow title="Contato inválido">
            <Cancel color="error" style={{ width: 16, height: 16 }} />
          </Tooltip>
        );

      case 'NAO_VERIFICADO':
        return (
          <Tooltip arrow title="Contato não verificado">
            <Cancel
              style={{
                width: 16,
                height: 16,
                color: colors.light.warning.main,
              }}
            />
          </Tooltip>
        );

      default:
        return null;
    }
  };

  return (
    <ListItem
      key={id}
      disableGutters
      dense
      ref={rowVirtualizer.measureElement}
      data-index={virtualRow.index}
    >
      <Paper
        elevation={0}
        className={clsx(classes.paper, {
          [classes.paperSelected]: selected,
        })}
        onClick={() => {
          onToggleActionSelection();
          handleExibirDetalhes(contato);
        }}
      >
        <Grid container spacing={1} wrap="wrap" alignItems="center">
          <Grid
            item
            style={{ display: 'flex' }}
            onClick={e => e.stopPropagation()}
          >
            <Box className={classes.avatarWrapper}>
              <ImageViewer src={fotoUrl}>
                <BlockedContactBadge isBlocked={isBlocked}>
                  <ContactAvatar src={fotoUrl} />
                </BlockedContactBadge>
              </ImageViewer>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md zeroMinWidth style={{ display: 'flex' }}>
            <PersonalListItemText
              primaryText="Nome"
              secondaryText={
                <Box
                  display="flex"
                  alignItems="center"
                  gridGap={8}
                  style={{ minWidth: 0 }}
                >
                  <BlockedContactName name={name} isBlocked={isBlocked} />

                  {contato.observation && (
                    <Tooltip arrow placement="top" title={contato.observation}>
                      <Info color="disabled" fontSize="small" />
                    </Tooltip>
                  )}
                </Box>
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md zeroMinWidth>
            <PersonalListItemText
              primaryText="Contato"
              secondaryText={
                <Box display="flex" alignItems="center" gridGap={8}>
                  <Box display="flex" alignItems="center">
                    {getIconTipoCanal({
                      tipoCanal,
                      size: 16,
                      isModoHibrido: contato.channel?.isHybridMode,
                    })}
                  </Box>
                  <Typography variant="body2" noWrap>
                    {displayContato}
                  </Typography>
                  {renderStatusIcon(status)}
                </Box>
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md zeroMinWidth>
            <PersonalListItemText
              primaryText="Atendente padrão"
              secondaryText={
                <Typography variant="body2" component="span" noWrap>
                  {getEquipeUsuarioLabel(contato)}
                </Typography>
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Box width="fit-content">
              <TagsV2
                id={contato.id}
                tags={contato.tags.map(t => ({
                  id: t.id,
                  nome: t.name,
                  cor: t.color,
                }))}
                isContato
              />
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            sm="auto"
            style={{ display: 'flex', alignItems: 'center' }}
            onClick={e => e.stopPropagation()}
          >
            <ContatoMenuAcoes
              contato={contato}
              setContato={setContatoSelected}
              onRefresh={onRefresh}
              onToggleSelection={onToggleActionSelection}
            />
          </Grid>
        </Grid>
      </Paper>
    </ListItem>
  );
}

function PersonalListItemText({
  primaryText,
  secondaryText,
}: {
  primaryText: React.ReactNode;
  secondaryText: React.ReactNode;
}) {
  return (
    <ListItemText
      primary={primaryText}
      secondary={secondaryText}
      primaryTypographyProps={{
        variant: 'caption',
        color: 'textSecondary',
      }}
      secondaryTypographyProps={{
        color: 'textPrimary',
        component: 'div',
        noWrap: true,
      }}
      style={{ margin: 0 }}
    />
  );
}
